import { NonTimezoneSensitiveDate } from '@commenda/utils';
import { useCallback } from 'react';
import { DATE_RANGES } from '../constants';

export function useDateValidation() {
  // Get maximum valid day for a given month/year
  const getMaxDayForMonth = useCallback((y: string, m: string): number => {
    if (!y || !m) return DATE_RANGES.MAX_DAY_FALLBACK;

    const yearNum = parseInt(y, 10);
    const monthNum = parseInt(m, 10);

    if (
      yearNum < DATE_RANGES.MIN_YEAR ||
      yearNum > DATE_RANGES.MAX_YEAR ||
      monthNum < DATE_RANGES.MIN_MONTH ||
      monthNum > DATE_RANGES.MAX_MONTH
    ) {
      return DATE_RANGES.MAX_DAY_FALLBACK;
    }

    // Try to get the last day of the month by creating a date for the first of next month
    // and subtracting one day. This automatically handles leap years.
    try {
      const nextMonth = monthNum === 12 ? 1 : monthNum + 1;
      const nextYear = monthNum === 12 ? yearNum + 1 : yearNum;
      const firstOfNextMonth = new NonTimezoneSensitiveDate(
        nextYear,
        nextMonth,
        1
      );
      const lastDayOfMonth = firstOfNextMonth.subtract(1, 'days');
      return lastDayOfMonth.getDate();
    } catch {
      // Fallback to known month lengths with leap year check for February
      const isLeapYear =
        (yearNum % 4 === 0 && yearNum % 100 !== 0) || yearNum % 400 === 0;
      const daysInMonth = [
        31, // January
        isLeapYear ? 29 : 28, // February (29 in leap years, 28 otherwise)
        31, // March
        30, // April
        31, // May
        30, // June
        31, // July
        31, // August
        30, // September
        31, // October
        30, // November
        31, // December
      ];
      return daysInMonth[monthNum - 1] || DATE_RANGES.MAX_DAY_FALLBACK;
    }
  }, []);

  // Check if a date is in the future
  const isFutureDate = useCallback(
    (y: string, m: string, d: string): boolean => {
      if (!y || !m || !d) return false;

      const yearNum = parseInt(y, 10);
      const monthNum = parseInt(m, 10);
      const dayNum = parseInt(d, 10);

      // Basic range validation - if invalid, don't report as future date
      if (
        yearNum < DATE_RANGES.MIN_YEAR ||
        yearNum > DATE_RANGES.MAX_YEAR ||
        monthNum < DATE_RANGES.MIN_MONTH ||
        monthNum > DATE_RANGES.MAX_MONTH ||
        dayNum < DATE_RANGES.MIN_DAY
      ) {
        return false;
      }

      try {
        const inputDate = new NonTimezoneSensitiveDate(
          yearNum,
          monthNum,
          dayNum
        );
        const today = NonTimezoneSensitiveDate.now();
        // gt() = greater than, meaning inputDate > today (future date)
        return inputDate.gt(today);
      } catch {
        return false;
      }
    },
    []
  );

  // Validate and format date from segments
  const validateAndFormatDate = useCallback(
    (y: string, m: string, d: string): string | null => {
      // If any segment is empty, return null (incomplete date)
      if (!y || !m || !d) {
        return null;
      }

      const yearNum = parseInt(y, 10);
      const monthNum = parseInt(m, 10);
      const dayNum = parseInt(d, 10);

      // Basic range validation
      if (
        yearNum < DATE_RANGES.MIN_YEAR ||
        yearNum > DATE_RANGES.MAX_YEAR ||
        monthNum < DATE_RANGES.MIN_MONTH ||
        monthNum > DATE_RANGES.MAX_MONTH ||
        dayNum < DATE_RANGES.MIN_DAY
      ) {
        return null;
      }

      // Check if day is valid for the month/year
      const maxDay = getMaxDayForMonth(y, m);
      if (dayNum > maxDay) {
        return null;
      }

      try {
        const ntsDate = new NonTimezoneSensitiveDate(yearNum, monthNum, dayNum);
        return ntsDate.toISO8601String();
      } catch {
        return null;
      }
    },
    [getMaxDayForMonth]
  );

  return {
    getMaxDayForMonth,
    validateAndFormatDate,
    isFutureDate,
  };
}
